import { IPhoto } from "../types/photo";

// Using 'Pothole_case' naming convention to match Samsung documentation.
const tail_start_signature = "SEFH";
const tail_end_signature = "SEFT";
const sizeof_tail_start_signature = tail_start_signature.length;
const sizeof_format_version = 4;
const sizeof_data_count = 4;
const sizeof_sub_data = 2;
const sizeof_data_type = 2;
const sizeof_start_position = 4;
const sizeof_data_length = 4;
const sizeof_data_reference_data_header = sizeof_sub_data + sizeof_data_type + sizeof_start_position + sizeof_data_length;
const sizeof_tail_header_offset = 4;
const sizeof_tail_end_signature = tail_end_signature.length;

const MotionPhotoDataType = 2608; // MP identifier
const MotionPhotoHeaderSize = 24; // Expected size of MP header
const MotionPhotoMinSupportedVersion = 101; // Earliest tested version

interface ISefDataReference {
  subData: number;
  dataType: number;
  startPosition: number; // startPosition is not measured from start of file, it is a count backwards from the tail header.
  dataLength: number;
}

interface ISefTail {
  startSignature: string;
  formatVersion: number;
  dataCount: number;
  dataReferences: ISefDataReference[];
  endSignature: string;
}

/**
 * Function to extract video from a motion photo
 *
 * @param blob
 * @returns
 */
export function getMotionVideo(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve: (buffer: ArrayBuffer) => void, reject) => {
    const fileReader = new FileReader();
    // Once the entire photo content is loaded we will create a large
    // data URL which is used to load the image into the editor.
    // It will also be used upon save to extract EXIF data and place
    // it back into the updated photo.

    fileReader.onload = () => {
      const buffer = fileReader.result as ArrayBuffer;

      try {
        const dataView = new DataView(buffer);

        // SEF file structure is described by ISefTail (see Samsung docs)
        const sefTail: ISefTail = {
          startSignature: "",
          formatVersion: 0,
          dataCount: 0,
          dataReferences: [],
          endSignature: ""
        };

        // Extract SEF tail signature
        const tailEndSigOffset = buffer.byteLength - sizeof_tail_end_signature;
        sefTail.endSignature = String.fromCharCode(
          dataView.getInt8(tailEndSigOffset),
          dataView.getInt8(tailEndSigOffset + 1),
          dataView.getInt8(tailEndSigOffset + 2),
          dataView.getInt8(tailEndSigOffset + 3)
        );

        // Extract size of SEF Tail.
        const seekBack = sizeof_tail_header_offset + sizeof_tail_end_signature;
        const tailSize = dataView.getUint32(buffer.byteLength - seekBack, true) + seekBack;

        // Extract the tail
        let tailStart = buffer.byteLength - tailSize;
        const formatStart = tailStart + sizeof_tail_start_signature;
        const dataCountStart = formatStart + sizeof_format_version;

        sefTail.startSignature = String.fromCharCode(
          dataView.getInt8(tailStart),
          dataView.getInt8(tailStart + 1),
          dataView.getInt8(tailStart + 2),
          dataView.getInt8(tailStart + 3)
        );
        sefTail.formatVersion = dataView.getUint32(formatStart, true);
        sefTail.dataCount = dataView.getUint32(dataCountStart, true);

        let dataReferenceOffset = tailStart + sizeof_tail_start_signature + sizeof_format_version + sizeof_data_count;
        for (let i = 0; i < sefTail.dataCount; ++i) {
          sefTail.dataReferences[i] = {
            subData: dataView.getUint16(dataReferenceOffset, true),
            dataType: dataView.getUint16(dataReferenceOffset + 2, true),
            // Note: startPosition is not measured from start of file, it is a count backwards from the tail header.
            startPosition: dataView.getUint32(dataReferenceOffset + 4, true),
            dataLength: dataView.getUint32(dataReferenceOffset + 8, true)
          };
          dataReferenceOffset += sizeof_data_reference_data_header;
        }

        // Validate the tail
        if (!validateTail(sefTail)) {
          reject("Invalid SEF: " + JSON.stringify(sefTail));
        } else {
          // Now compute the size of Header segment for the Motion Photo block
          const offsetOfSegHeaderFromEOF = tailSize + sefTail.dataReferences[sefTail.dataCount - 1].dataLength;
          const motionPhotoKeyNameLength = dataView.getUint32(buffer.byteLength - offsetOfSegHeaderFromEOF + 4, true);
          const motionPhotoHeaderSize = 8 + motionPhotoKeyNameLength;

          if (motionPhotoHeaderSize !== MotionPhotoHeaderSize) {
            reject("Unexpected motionPhotoHeaderSize: " + motionPhotoHeaderSize);
          } else {
            // Now we can determine the offset of the mp4
            const mp4OffsetFromEOF = tailSize + sefTail.dataReferences[sefTail.dataCount - 1].dataLength - motionPhotoHeaderSize;
            const mp4Offset = buffer.byteLength - mp4OffsetFromEOF;

            // Extract the mp4 buffer
            const mp4Buffer = buffer.slice(mp4Offset);

            if (!validateMP4Buffer(mp4Buffer)) {
              reject("Invalid MP4");
            } else {
              resolve(mp4Buffer);
            }
          }
        }
      } catch (e) {
        reject(e);
      }
    };

    fileReader.readAsArrayBuffer(blob);
  });
}

const editableImageTypes = ["image/jpeg", "image/png"];
/**
 * Function to check if a photo is editable and if the user has permissions to edit the photo
 *
 * @param driveId Current sessions driveId
 * @param photo IPhoto
 * @returns
 */
export function photoEditable(driveId: string, photo?: IPhoto): boolean {
  if (!photo) {
    return false;
  }

  const fileType = photo.file?.mimeType ?? "";
  const editableImageType = editableImageTypes.includes(fileType) || (fileType.includes("heic") && !!photo.image);

  const accessViewpoint = photo.accessViewpoint;
  if (accessViewpoint) {
    return accessViewpoint.canEdit && editableImageType;
  }

  const shared = photo.parentReference?.driveId !== driveId;
  return !shared && editableImageType;
}

function validateMP4Buffer(buffer: ArrayBuffer): boolean {
  let valid: boolean = false;
  const dataView = new DataView(buffer);

  // Do a simple verification that the blob has the
  // expected mp4 ftyp header, leaving more robust
  // format validity checking to a video player.
  if (buffer.byteLength > 8) {
    const offset = 4;
    const expected = "ftyp"; // Every mp4 has this 4-byte filetype header at offset 4
    const actual = String.fromCharCode(
      dataView.getInt8(offset),
      dataView.getInt8(offset + 1),
      dataView.getInt8(offset + 2),
      dataView.getInt8(offset + 3)
    );

    valid = actual === expected;
  }

  return valid;
}

// Error check the SEF tail contents
function validateTail(tail: ISefTail): boolean {
  return (
    tail.startSignature === tail_start_signature &&
    tail.endSignature === tail_end_signature &&
    tail.formatVersion >= MotionPhotoMinSupportedVersion &&
    tail.dataCount > 0 &&
    tail.dataReferences[tail.dataCount - 1].dataType === MotionPhotoDataType
  );
}
